import CloseIcon from '@mui/icons-material/Close';
import DarkModeOutlinedIcon from '@mui/icons-material/DarkModeOutlined';
import LogoutIcon from '@mui/icons-material/Logout';
import SettingsIcon from '@mui/icons-material/Settings';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import MenuItem from '@mui/material/MenuItem';
import MenuList from '@mui/material/MenuList';
import Stack from '@mui/material/Stack';
import { useColorScheme } from '@mui/material/styles';
import Switch from '@mui/material/Switch';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';
import { Link, useRouterState } from '@tanstack/react-router';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { SETTINGS_BASE_PATH } from '../../features/settings/routes';
import { SUPPORTED_LANGUAGES, type SupportedLanguage } from '../../i18n';
import { useCurrentUser, useLogoutMutation, type PartnerCockpitUser } from '../../lib/auth';
import { LANGUAGE_LABELS, languageCode, useLanguageSelection } from '../../lib/language';
import { useBodyScrollLock } from '../../lib/useBodyScrollLock';
import { stateColor } from '../../theme';
import { isNavItemActive, type HeaderNavItem } from './navigation';

const DRAWER_WIDTH = 320;
const LANGUAGE_BUTTON_WIDTH = 40;

export type NavigationDrawerProps = {
    open: boolean;
    onClose: () => void;
    navItems: HeaderNavItem[];
};

function initialsOf(user: PartnerCockpitUser): string {
    const initials = `${user.firstName?.trim().charAt(0) ?? ''}${user.lastName?.trim().charAt(0) ?? ''}`.toUpperCase();
    return initials.length > 0 ? initials : user.email.charAt(0).toUpperCase();
}

function displayNameOf(user: PartnerCockpitUser): string | undefined {
    const name = [user.firstName, user.lastName]
        .map((part) => part?.trim())
        .filter((part) => part)
        .join(' ');
    return name.length > 0 ? name : undefined;
}

export function NavigationDrawer({ open, onClose, navItems }: NavigationDrawerProps): ReactElement {
    const { t } = useTranslation();
    const pathname = useRouterState({ select: (s) => s.location.pathname });
    const user = useCurrentUser();
    const logout = useLogoutMutation();
    const { mode, setMode } = useColorScheme();
    const { activeLanguage, select: selectLanguage } = useLanguageSelection();

    const name = user ? displayNameOf(user) : undefined;

    useBodyScrollLock(open);

    const handleLogout = (): void => {
        onClose();
        logout.mutate();
    };

    return (
        <Drawer
            anchor="left"
            open={open}
            onClose={onClose}
            slotProps={{
                paper: {
                    sx: {
                        width: DRAWER_WIDTH,
                        height: '100dvh',
                        overflowY: 'auto',
                        overscrollBehavior: 'contain',
                        '& > *': { flexShrink: 0 },
                        borderTopRightRadius: 1,
                        borderBottomRightRadius: 1,
                        py: 3,
                    },
                },
            }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 2, pb: 4 }}>
                <Box
                    component="img"
                    src="/houzy_logo.svg"
                    alt={t('HEADER.LOGO_ALT')}
                    sx={{ height: 28, width: 96, objectFit: 'contain' }}
                />
                <IconButton onClick={onClose} aria-label={t('HEADER.CLOSE_MENU_ARIA')}>
                    <CloseIcon />
                </IconButton>
            </Stack>

            {user ? (
                <>
                    <Stack direction="row" alignItems="center" spacing={2} sx={{ p: 2 }}>
                        <Avatar>{initialsOf(user)}</Avatar>
                        <Stack sx={{ minWidth: 0 }}>
                            {name ? (
                                <Typography variant="subtitle1" noWrap>
                                    {name}
                                </Typography>
                            ) : undefined}
                            <Typography variant="body2" color="text.secondary" noWrap>
                                {user.email}
                            </Typography>
                        </Stack>
                    </Stack>
                    <Divider />
                </>
            ) : undefined}

            <MenuList sx={{ px: 1 }} aria-label={t('HEADER.PRIMARY_NAV')}>
                {navItems.map((item) => {
                    const active = isNavItemActive(item, pathname);
                    return (
                        <MenuItem
                            key={item.to}
                            component={Link}
                            to={item.to}
                            onClick={onClose}
                            aria-current={active ? 'page' : undefined}
                            sx={{
                                borderRadius: 1,
                                py: 1.5,
                                color: active ? 'primary.main' : 'text.primary',
                                bgcolor: active ? (theme): string => stateColor(theme, 'hover') : 'transparent',
                            }}>
                            {item.label}
                        </MenuItem>
                    );
                })}
            </MenuList>
            <Divider />

            <MenuList sx={{ px: 1 }}>
                <MenuItem component={Link} to={SETTINGS_BASE_PATH} onClick={onClose} sx={{ borderRadius: 1, py: 1.5 }}>
                    <ListItemIcon>
                        <SettingsIcon />
                    </ListItemIcon>
                    {t('PROFILE.SETTINGS')}
                </MenuItem>
                <MenuItem onClick={handleLogout} disabled={logout.isPending} sx={{ borderRadius: 1, py: 1.5 }}>
                    <ListItemIcon>
                        <LogoutIcon />
                    </ListItemIcon>
                    {t('PROFILE.LOG_OUT')}
                </MenuItem>
            </MenuList>
            <Divider />

            <Stack sx={{ px: 1, pt: 1 }}>
                <Stack direction="row" alignItems="center" spacing={2} sx={{ px: 2, py: 0.75 }}>
                    <DarkModeOutlinedIcon />
                    <Typography sx={{ flexGrow: 1 }}>{t('HEADER.DARK_MODE')}</Typography>
                    <Switch
                        checked={mode === 'dark'}
                        onChange={(_, checked) => setMode(checked ? 'dark' : 'light')}
                        slotProps={{ input: { 'aria-label': t('HEADER.THEME_ARIA') } }}
                    />
                </Stack>
                <Box sx={{ p: 1 }}>
                    <ToggleButtonGroup
                        exclusive
                        size="small"
                        value={activeLanguage}
                        aria-label={t('HEADER.LANGUAGE_ARIA')}
                        onChange={(_, language: SupportedLanguage | null) => {
                            // `exclusive` reports null when the active button is pressed again; the language has to stay set.
                            if (language) {
                                void selectLanguage(language);
                            }
                        }}>
                        {SUPPORTED_LANGUAGES.map((language) => (
                            <ToggleButton
                                key={language}
                                value={language}
                                aria-label={LANGUAGE_LABELS[language]}
                                sx={{ width: LANGUAGE_BUTTON_WIDTH }}>
                                {languageCode(language)}
                            </ToggleButton>
                        ))}
                    </ToggleButtonGroup>
                </Box>
            </Stack>
        </Drawer>
    );
}
